<template>
  <div class="list-page">
    <div class="page-header">
      <h1>{{ pageTitle }}</h1>
      <p class="page-description">{{ pageDescription }}</p>
    </div>

    <p v-if="articles.length === 0" class="empty-state">No articles found.</p>

    <div v-else>
      <!-- Featured Hero -->
      <FeaturedHero v-if="articles.length > 0" :article="articles[0]" />

      <!-- Article Grid: all remaining articles -->
      <div class="articles-grid">
        <ArticleCard
          v-for="article in articles.slice(1)"
          :key="article.slug"
          :article="article"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { generateListPageMetaInfo, buildBreadcrumbs } from '@/utils/seo'
import FeaturedHero from '@/components/home/FeaturedHero.vue'
import ArticleCard from '@/components/article/ArticleCard.vue'

export default {
  name: 'NewsListPage',
  components: {
    FeaturedHero,
    ArticleCard,
  },
  asyncData({ store, route }) {
    const category = route.meta.category || route.params.category
    const subcategory = route.meta.subcategory || route.params.subcategory
    return store.dispatch('articles/fetchArticles', { category, subcategory })
  },
  computed: {
    articles() {
      return this.$store.state.articles.articles
    },
    pageTitle() {
      const category = this.$route.meta.category || this.$route.params.category
      const subcategory = this.$route.meta.subcategory || this.$route.params.subcategory
      if (subcategory) {
        return `${category} - ${subcategory}`
      }
      return this.$route.meta.title || category
    },
    pageDescription() {
      const category = this.$route.meta.category || this.$route.params.category
      const subcategory = this.$route.meta.subcategory || this.$route.params.subcategory

      const descriptions = {
        news: 'Stay updated with the latest gaming news, announcements, and industry updates.',
        reviews: 'In-depth game reviews and hardware analysis from our expert team.',
        guides: 'Comprehensive gaming guides, tips, tricks, and walkthroughs.',
        deals: 'Find the best gaming deals, discounts, and special offers.',
        games: 'Explore our collection of popular games and gaming content.',
        platforms: 'Gaming platform guides, news, and recommendations.',
      }

      if (subcategory) {
        return `Browse ${subcategory} ${category} articles and content.`
      }

      return descriptions[category] || `Browse ${category} articles and content.`
    },
  },
  methods: {
    generateListPageMetaInfo,
  },
  metaInfo() {
    const breadcrumbs = buildBreadcrumbs(this.$route, {
      subcategory: this.$route.meta.subcategory || this.$route.params.subcategory,
    })

    return generateListPageMetaInfo({
      title: this.pageTitle,
      description: this.pageDescription,
      path: this.$route.path,
      breadcrumbs,
    })
  },
}
</script>

<style lang="scss" scoped>
@use '@/css/variables.scss' as *;

.list-page {
  max-width: 1200px;
  margin: 0 auto;
  padding: $spacing-xl $spacing-lg;
}

.page-header {
  margin-bottom: $spacing-xl;

  h1 {
    font-size: $font-size-xxl;
    font-weight: 800;
    color: $text-primary;
    margin: 0 0 $spacing-md 0;
    text-transform: capitalize;
  }

  .page-description {
    font-size: $font-size-lg;
    color: $text-secondary;
    line-height: 1.6;
    margin: 0;
  }
}

.articles-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: $spacing-lg;
  margin-top: $spacing-xl;

  @media (max-width: 900px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
}

.empty-state {
  text-align: center;
  color: $text-secondary;
  padding: $spacing-xl * 3 0;
  font-size: $font-size-lg;
}
</style>
